import React, { useEffect, useMemo } from 'react';
import { Loading } from '../../../../../base/enums';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';
import { HireStatus, ResponseType } from '../../enums';
import { responsesActions } from '../../redux/slices/responsesSlice';
import { VacancyItemAll } from '../FilterSearch/DesktopSelects/VacancySelect';
import { NoResults } from './NoResults';
import { Pagination } from '../../../../../base/components/Pagination';
import { TableItem } from './TableItem';

import './styles.scss';
import { GetResponsesPerPageParam } from '../../api/interfaces';

interface Props {
    type: ResponseType;
}

export const ResponsesTable = ({ type }: Props) => {
    const dispatch = useAppDispatch();

    const responses = useAppSelector((state) => state.employerResponses.responses);
    const currencies = useAppSelector((state) => state.employerApp.config.currency);
    const isDataLoading = useAppSelector((state) => state.app.loading === Loading.App);
    const isDefaultFilterSelected = useAppSelector(
        (state) =>
            state.employerResponses.filters.hireStatus === HireStatus.All &&
            state.employerResponses.filters.vacancyId === VacancyItemAll.id &&
            !state.employerResponses.filters.dateFrom &&
            !state.employerResponses.filters.dateTo,
    );
    const { currentPage, totalCount, perPage } = useAppSelector((state) => state.employerResponses.pagination);

    useEffect(() => {
        dispatch(responsesActions.setResponseType(type));
    }, [type]);

    const tableContent = useMemo(() => {
        if (!responses.length) {
            return <NoResults type={type} loading={isDataLoading} showEmptyFilterResult={!isDefaultFilterSelected} />;
        }

        return responses.map((response) => (
            <TableItem key={response.id} type={type} data={response} currencies={currencies} />
        ));
    }, [responses, type, isDataLoading, isDefaultFilterSelected]);

    const paginationHandler = (page: number) => {
        dispatch(responsesActions.setPaginationPage(page));
        dispatch(responsesActions.getResponses());
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const perPageChangeHandler = (value: number) => {
        dispatch(responsesActions.setPaginationPerPage(value as GetResponsesPerPageParam));
        dispatch(responsesActions.getResponses());
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    return (
        <div className='responses-table'>
            <div className='razem-app__wrapper responses-table__wrapper'>
                <div className='responses-table__scroll-wrap'>
                    <div className='responses-table__header'>
                        <div className='responses-table__header-column responses-table__column responses-table__date-column'>
                            {__t('Дата')}
                        </div>
                        <div className='responses-table__header-column responses-table__column responses-table__worker-column'>
                            {__t('Робітник')}
                        </div>
                        <div className='responses-table__header-column responses-table__column responses-table__experience-column'>
                            {__t('Досвід')}
                        </div>
                        <div className='responses-table__header-column responses-table__column responses-table__vacancy-column'>
                            {__t('Вакансія')}
                        </div>
                        <div className='responses-table__header-column responses-table__column responses-table__contacts-column'>
                            {__t('Контакти')}
                        </div>
                        <div className='responses-table__header-column responses-table__column responses-table__interactions-column'></div>
                    </div>

                    <div className='responses-table__content'>{tableContent}</div>
                </div>

                {!!responses.length && (
                    <Pagination
                        currentPage={currentPage}
                        totalCount={totalCount}
                        perPageValue={perPage}
                        perPageOptions={[10, 20, 30, 40, 50]}
                        paginationHandler={paginationHandler}
                        perPageChangeHandler={perPageChangeHandler}
                    />
                )}
            </div>
        </div>
    );
};
